import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Button, ButtonType } from '@/components/atoms/button'
import { DeleteIcon } from '@/components/molecules/settings/icon/delete-icon'
import { useDraggableDom } from '@/components/molecules/settings/use-draggable-dom'
import { TableBody, TableHeader, TableRow } from '@/components/organisms/settings/sales-manager/table'
import type { RestaurantResolvedDisplaySubject } from '@/models/restaurant-manager'

type Props = {
  value: RestaurantResolvedDisplaySubject[]
  canAdd: boolean
  canEditSubjects: boolean
  onAdd: () => void
  onEdit: (subject: RestaurantResolvedDisplaySubject) => void
  onDelete: (subjectId: string) => void
  onChange: (nextValue: RestaurantResolvedDisplaySubject[]) => void
}

export const DisplaySubjectPane: React.FC<Props> = ({ value, canAdd, canEditSubjects, onAdd, onEdit, onDelete, onChange }) => {
  const { t } = useTranslation()
  const { dragStart, dragOver, dragDrop, handleTouchStart, handleTouchMove, handleTouchEnd } = useDraggableDom({
    list: value,
    onChange,
    key: 'subjectId',
  })

  return (
    <section css={containerStyle}>
      <div css={headerStyle}>
        {canAdd ? (
          <Button
            buttonType={canEditSubjects ? ButtonType.Primary : ButtonType.PrimaryDisabled}
            width={107}
            height={32}
            fontSize={12}
            isDisabled={!canEditSubjects}
            onClick={onAdd}
            icon={require('@/static/images/plus-white.svg')}
          >
            {t('Add new')}
          </Button>
        ) : (
          <div />
        )}
      </div>
      <TableHeader css={tableStyle}>
        <div className="subject-col">{t('RestaurantManager.DisplaySubjectName')}</div>
        <div className="display-day-col">{t('RestaurantManager.DisplayDay')}</div>
        <div className="delete-col" />
      </TableHeader>
      <TableBody>
        {value.length > 0 ? (
          value.map(subject => (
            <TableRow
              key={subject.subjectId}
              id={subject.subjectId}
              draggable={canEditSubjects ? 'true' : undefined}
              css={[tableStyle, !canEditSubjects ? disabledRowStyle : undefined]}
              onClick={() => {
                if (!canEditSubjects) {
                  return
                }
                onEdit(subject)
              }}
              onDragStart={dragStart}
              onDragOver={dragOver}
              onDrop={dragDrop}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="subject-col">{subject.subjectName}</div>
              <div className="display-day-col">
                {t(`RestaurantManager.DisplayDay${subject.displayDay === 'sameDay' ? 'SameDay' : 'NextDay'}`)}
              </div>
              <div className="delete-col">
                <DeleteIcon
                  disabled={!canEditSubjects}
                  onClick={() => {
                    onDelete(subject.subjectId)
                  }}
                />
              </div>
            </TableRow>
          ))
        ) : (
          <div css={emptyStyle}>{t('No data available')}</div>
        )}
      </TableBody>
    </section>
  )
}

const containerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
})

const headerStyle = css({
  alignItems: 'center',
  borderBottom: '1px solid #F2F2F2',
  display: 'flex',
  flexShrink: 0,
  height: 75,
  justifyContent: 'space-between',
  padding: '0 32px',
})

const tableStyle = css({
  '.subject-col': {
    flex: 1,
    marginLeft: 32,
    minWidth: 0,
    overflowWrap: 'anywhere',
    paddingRight: 16,
  },
  '.display-day-col': {
    width: 72,
  },
  '.delete-col': {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '0 32px 0 24px',
    width: 30,
  },
  '&[draggable="true"] .subject-col': {
    paddingLeft: 20,
  },
})

const disabledRowStyle = css({
  cursor: 'default',
})

const emptyStyle = css({
  alignItems: 'center',
  color: '#676767',
  display: 'flex',
  height: 120,
  justifyContent: 'center',
})
